/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Alert, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useErrorHandler } from 'components/error'
import { PrivateRoute } from 'components/route'
import { differenceInCalendarDays } from 'date-fns'
import {
  useListaAtendimentoQuery,
  usePossuiAtendimentosNaoFinalizadosQuery,
  useUltimoAtendimentoProfissionalCidadaoQuery,
} from 'graphql/hooks.generated'
import { PageParams, StatusAtendimento } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { usePubSub } from 'hooks/pubsub/usePubSub'
import { useServerTime } from 'hooks/useServerTime'
import { useLocalStorageState } from 'hooks/useStorage'
import { useUrlQueryParams } from 'hooks/useUrlQueryParams'
import { isEmpty } from 'lodash'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Switch, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import AtendimentoListing from 'view/atendimentos/components/AtendimentoListing'
import { AdicionarCidadaoButton } from 'view/atendimentos/list/AdicionarCidadaoButton'
import { ExibindoSomenteNaoFinalizadosAlert } from 'view/atendimentos/list/alerts/ExibindoSomenteNaoFinalizadosAlert'
import { ExistemAtendimentosNaoFinalizadosAlert } from 'view/atendimentos/list/alerts/ExistemAtendimentosNaoFinalizadosAlert'
import { useListaAtendimentosNaoFinalizadosAlert } from 'view/atendimentos/list/hooks/useListaAtendimentosNaoFinalizadosAlert'
import { usePeriodoParaAtendimentosNaoFinalizados } from 'view/atendimentos/list/hooks/usePeriodoParaAtendimentosNaoFinalizados'
import { ListaAtendimentoFooter } from 'view/atendimentos/list/ListaAtendimentoFooter'
import {
  AtendimentosRegistroTardioFilterModel,
  useListaRegistroTardioFilterDefault,
} from 'view/registro-tardio/filter/useListaRegistroTardioFilterDefault'

import { EditarRegistroTardioModalForm } from './components/modal/EditarRegistroTardioModalForm'
import { RegistroTardioAtendimentoPosteriorModal } from './components/modal/RegistroTardioAtendimentoPosteriorModal'
import { ListaRegistroTardioFilter, ListaRegistroTardioFilterModel } from './filter/ListaRegistroTardioFilter'
import { ListaRegistroTardioFilterPopperModel } from './filter/ListaRegistroTardioFilterPopperForm'
import ListaRegistroTardioForm from './ListaRegistroTardioForm'
import { ListaRegistroTardioItem } from './ListaRegistroTardioItem'
import { convertListaRegistroTardioStateToInput } from './utils-registroTardio'

type AtendimentoTopicModel = { idUbs: number }

interface RegistroTardioUrlParams {
  cidadaoId: string
}

export function ListaRegistroTardioView() {
  const handleRejection = useErrorHandler()
  const { getServerTimeNow } = useServerTime()
  const { acesso } = useAcessoLotacaoOrEstagio()
  const { analytics } = useFirebase()
  const match = useRouteMatch()

  const acessoId = acesso?.id

  const { cidadaoId: cidadaoIdFromURL } = useUrlQueryParams<RegistroTardioUrlParams>()

  const filterDefault = useListaRegistroTardioFilterDefault()

  const currentServerTime = getServerTimeNow().getTime()

  const lastUpdateRef = useRef<Instant>()

  const [atendimentoMaisRecenteConflitante, setAtendimentoMaisRecenteConflitante] = useState(null)

  const { refetch: fetchAtendimentoMaisRecente } = useUltimoAtendimentoProfissionalCidadaoQuery({ skip: true })

  const verificarExisteAtendimentoMaisRecente = async (dataAtendimento: Instant, cidadaoId: ID) => {
    const ultimoAtendimento = await fetchAtendimentoMaisRecente({
      input: { cidadaoId, onlyAtendimentosEvoluemProntuario: true },
    })
    const possuiAtendimentoMaisRecente =
      ultimoAtendimento?.data?.ultimoAtendimentoProfissionalCidadao?.finalizadoEm > dataAtendimento
    if (possuiAtendimentoMaisRecente) {
      setAtendimentoMaisRecenteConflitante(ultimoAtendimento?.data?.ultimoAtendimentoProfissionalCidadao)
    }

    return !possuiAtendimentoMaisRecente
  }

  const [state, setState] = useLocalStorageState<AtendimentosRegistroTardioFilterModel>(
    `${acessoId}/registro-tardio-state`,
    { defaultValue: filterDefault }
  )

  const isSomenteNaoFinalizados = state?.filter?.isSomenteNaoFinalizados

  const [backupFilterStorage, setBackupFilterStorage, deleteBackupFilterStorage] = useLocalStorageState<
    ListaRegistroTardioFilterPopperModel
  >(`${acessoId}/registro-tardio-filter-backup`)

  const {
    marcouAlertComoVisto,
    deveMostrarAlertNovamente,
    setAlertComoVisto,
  } = useListaAtendimentosNaoFinalizadosAlert(state.lastDailyUpdate, true)

  const periodoParaNaoFinalizados = usePeriodoParaAtendimentosNaoFinalizados(state.lastUpdate)

  const { data: possuiAtendimentosNaoFinalizados } = usePossuiAtendimentosNaoFinalizadosQuery({
    variables: {
      input: {
        startDate: periodoParaNaoFinalizados.startDate,
        endDate: periodoParaNaoFinalizados.endDate,
        isRegistroTardio: true,
      },
    },
    skip: marcouAlertComoVisto && !deveMostrarAlertNovamente,
  })

  const { data, refetch } = useListaAtendimentoQuery({
    variables: {
      input: convertListaRegistroTardioStateToInput(state, filterDefault.filter.periodo),
    },
  })

  const refetchLista = useCallback(() => {
    refetch()
      .then(() => {
        setState((prevState) => ({
          ...prevState,
          lastUpdate: currentServerTime,
        }))
      })
      .catch(handleRejection)
  }, [currentServerTime, handleRejection, refetch, setState])

  usePubSub<AtendimentoTopicModel>({
    topic: `/registroTardio/${acesso?.unidadeSaude.id}`,
    onMessage: (data) => data?.idUbs && refetchLista(),
  })

  const onChangeFilter = useCallback(
    (filter: ListaRegistroTardioFilterModel) => {
      setState((prevState) => ({
        ...prevState,
        filter,
        pageParams: {
          ...prevState.pageParams,
          page: 0,
        },
        lastUpdate: currentServerTime,
      }))
    },
    [currentServerTime, setState]
  )

  const onChangeFooter = useCallback(
    (pageParams: PageParams) => {
      setState((prevState) => ({ ...prevState, pageParams }))
    },
    [setState]
  )

  const handleClear = useCallback(() => {
    deleteBackupFilterStorage()
    onChangeFilter(filterDefault.filter)
  }, [deleteBackupFilterStorage, filterDefault.filter, onChangeFilter])

  const onClickSomenteRegistrosNaoFinalizados = useCallback(() => {
    onChangeFilter({
      ...filterDefault.filter,
      isSomenteNaoFinalizados: true,
      periodo: { startDate: undefined, endDate: undefined },
      statusAtendimento: [StatusAtendimento.EM_ATENDIMENTO],
    })

    setAlertComoVisto()
  }, [filterDefault.filter, onChangeFilter, setAlertComoVisto])

  const resetFilterToInitialState = useCallback(() => {
    setState((prevState) => {
      return {
        ...prevState,
        filter: {
          ...prevState?.filter,
          query: undefined,
        },
      } as AtendimentosRegistroTardioFilterModel
    })
  }, [setState])

  useEffect(() => {
    deleteBackupFilterStorage()
    resetFilterToInitialState()
  }, [resetFilterToInitialState, deleteBackupFilterStorage])

  useEffect(() => {
    lastUpdateRef.current = state.lastUpdate
  }, [state.lastUpdate])

  useEffect(() => {
    if (!state.lastDailyUpdate || differenceInCalendarDays(currentServerTime, new Date(state.lastDailyUpdate)) > 0) {
      if (!isEmpty(backupFilterStorage)) {
        setBackupFilterStorage({ ...backupFilterStorage, periodo: filterDefault.filter.periodo })
      }

      setState((prevValue) => ({
        ...prevValue,
        filter: {
          ...prevValue.filter,
          periodo: filterDefault.filter.periodo,
        },
        lastDailyUpdate: currentServerTime,
      }))
    }
  }, [backupFilterStorage, filterDefault.filter.periodo, currentServerTime, setBackupFilterStorage, setState, state])

  const renderHeading = (
    <VFlow>
      <Alert type='info'>
        Registre os atendimentos na ordem cronológica em que ocorreram. Registros anteriores ao último atendimento não
        serão possíveis.
      </Alert>
      {!!isSomenteNaoFinalizados && (
        <ExibindoSomenteNaoFinalizadosAlert atendimentosOuRegistros='registros' onClear={handleClear} />
      )}
      {possuiAtendimentosNaoFinalizados?.possuiAtendimentosNaoFinalizados &&
        !isSomenteNaoFinalizados &&
        (!marcouAlertComoVisto || deveMostrarAlertNovamente) && (
          <ExistemAtendimentosNaoFinalizadosAlert
            atendimentosOuRegistros='registros'
            onClickExibirSomenteNaoFinalizados={onClickSomenteRegistrosNaoFinalizados}
            onClickManterFiltroAtual={setAlertComoVisto}
          />
        )}
      <CheckPermission permission={Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir}>
        <AdicionarCidadaoButton
          cidadaoId={cidadaoIdFromURL}
          dataTestId='adicionarCidadao'
          formComponent={
            <ListaRegistroTardioForm
              onSubmit={verificarExisteAtendimentoMaisRecente}
              serverTime={new Date(currentServerTime)}
              cidadaoIdFromURL={cidadaoIdFromURL}
            />
          }
          onClick={() => analytics.logEvent('adicionar_cidadao_LRT')}
        />
      </CheckPermission>
    </VFlow>
  )

  const renderFilter = (
    <ListaRegistroTardioFilter
      filter={state.filter}
      filterBackup={backupFilterStorage}
      filterDefault={filterDefault.filter}
      onChangeFilter={onChangeFilter}
      updateFilterBackup={setBackupFilterStorage}
      deleteFilterBackup={() => deleteBackupFilterStorage()}
      onClear={handleClear}
    />
  )

  return (
    <Fragment>
      <Switch>
        <PrivateRoute
          path={`${match.url}/editar/:atendimentoId(\\d+)`}
          permission={Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir}
        >
          <EditarRegistroTardioModalForm
            cidadaoIdFromURL={cidadaoIdFromURL}
            onSubmit={verificarExisteAtendimentoMaisRecente}
          />
        </PrivateRoute>
      </Switch>
      <AtendimentoListing
        title='Registro tardio de atendimento'
        heading={renderHeading}
        filter={renderFilter}
        footer={
          <ListaAtendimentoFooter
            pageInfo={data.atendimentos?.pageInfo}
            pageParams={state.pageParams}
            onChange={onChangeFooter}
          />
        }
      >
        {!!atendimentoMaisRecenteConflitante && (
          <RegistroTardioAtendimentoPosteriorModal
            open
            onClose={() => setAtendimentoMaisRecenteConflitante(null)}
            atendimento={atendimentoMaisRecenteConflitante}
          />
        )}
        {data.atendimentos && data.atendimentos.content.length !== 0 ? (
          data.atendimentos.content.map((atend, key) => (
            <ListaRegistroTardioItem onClick={verificarExisteAtendimentoMaisRecente} key={key} atend={atend} />
          ))
        ) : (
          <div style={{ marginTop: '0.5rem' }}> Nenhum resultado encontrado.</div>
        )}
      </AtendimentoListing>
    </Fragment>
  )
}
